import * as React from 'react';
import { FormatDate, formatFieldValue, formatValue, notUndefined } from '../../utils/common';
import { Resource, ResourceEntityState, ResourceStatus, createResourceCalendar, findUsage } from '../../../store/ResourcesListStore';
import { PersonaSize, Icon } from 'office-ui-fabric-react';
import { ApplicationState } from '../../../store';
import { connect } from 'react-redux';
import { calculateSummary, ISummaryInfo } from './ResourceUsageCell';
import { post } from '../../../fetch-interceptor';
import { ImagePersonaCoin } from '../../common/ImagePersona';
import Link from '../../common/Link';
import { defaultCatch } from '../../../store/utils';
import { Dictionary, EntityType, IEditableExtensibleEntityWithResourcePlan, entityTypeLabelMap, mapServerEntityType } from '../../../entities/common';
import { FormatType } from '../../../entities/Metadata';
import { ResourceBookingType, calculateStateInEntity } from './ResourceUsageGrid';
import { StatusView } from '../../views/list/columns/resource/Status';
import { StateInEntityView } from '../../views/list/columns/resource/StateInEntity';
import { TenantState } from '../../../store/Tenant';
import { UserState } from '../../../store/User';
import { getDefaultCostRateTable } from '../../../store/selectors/costRateTableSelectors';
import { CalendarDataSet } from '../../../store/calendar/common';
import { getEntityCalendar } from '../../../store/CalendarsListStore';

type StateProps = {
    baseResourceCalendar: CalendarDataSet;
    resourcePlanningLevel: EntityType;
    tenant: TenantState;
    user: UserState;
    defaultCostRateTableId?: string;
}

export type OwnProps = {
    resource: Resource;
    startDate: Date;
    finishDate: Date;
    entityId?: string;
    entityType: EntityType;
    onMenuRender?: () => JSX.Element;
    showPerEntityWork?: boolean;
};

type Props = OwnProps & StateProps;
type State = { summary: ISummaryInfo, knownEntitiesMap: Dictionary<IEditableExtensibleEntityWithResourcePlan> };

class UtilizationTooltipContent extends React.Component<Props, State> {
    constructor(props: Props) {
        super(props);
        const { resource, startDate, finishDate, entityId, baseResourceCalendar, showPerEntityWork, entityType, resourcePlanningLevel, defaultCostRateTableId } = props;
        const summary = calculateSummary(resource, startDate, finishDate, baseResourceCalendar, entityType, resourcePlanningLevel, defaultCostRateTableId);
        this.state = {
            summary,
            knownEntitiesMap: {}
        };

        if (showPerEntityWork) {
            const unknownEntityIds = entityId
                ? Object.keys(summary.committedByEntity).filter(_ => _ !== entityId && !!summary.committedByEntity[_])
                : [
                    ...Object.keys(summary.committedByEntity).filter(_ => !!summary.committedByEntity[_]),
                    ...Object.keys(summary.proposedByEntity).filter(_ => !!summary.proposedByEntity[_])
                ];

            if (unknownEntityIds.length) {
                post<IEditableExtensibleEntityWithResourcePlan[]>(`api/resourcePlan/find`, { entityType: resourcePlanningLevel, ids: unknownEntityIds })
                    .then(entities => {
                        this.setState({
                            knownEntitiesMap: entities.reduce((cum, cur) => ({ ...cum, [cur.id]: cur }), {})
                        });
                    })
                    .catch(defaultCatch());
            }
        }
    }

    componentWillReceiveProps(nextProps: Props) {
        const { resource, startDate, finishDate, baseResourceCalendar, entityType, resourcePlanningLevel, defaultCostRateTableId } = nextProps;
        this.setState({ summary: calculateSummary(resource, startDate, finishDate, baseResourceCalendar, entityType, resourcePlanningLevel, defaultCostRateTableId) });
    }

    render() {
        const { resource, startDate, finishDate, entityId, baseResourceCalendar, entityType, tenant, user } = this.props;
        const { summary } = this.state;
        const { capacityHours, availability, overage, hasLocalOverage, committedByEntity, proposedByEntity, committedWork, proposedWork, actualWork,
            proposed_overage, proposed_hasLocalOverage, proposed_overage_byEntity, proposed_hasLocalOverage_byEntity } = summary;
        const proposedOverage = entityId ? (proposed_overage_byEntity[entityId] ?? overage) : proposed_overage;
        const hasLocalProposedOverage = entityId ? proposed_hasLocalOverage_byEntity[entityId] : proposed_hasLocalOverage;

        const isSingleDay = startDate.getBeginOfDay().getTime() == finishDate.getBeginOfDay().getTime();
        const resourceCalendar = createResourceCalendar(resource, baseResourceCalendar);
        const calendarException = isSingleDay && resourceCalendar.exceptionsHoursMap[startDate.getBeginOfDay().getTime()] !== undefined
            ? resourceCalendar.exceptions.find(_ => _.start <= startDate && startDate <= _.end && _.days.includes(startDate.getDay()))
            : undefined;

        const effectiveUsage = entityId
            ? (summary.effectiveUsageByEntity[entityId] ?? summary.effectiveUsage)
            : summary.effectiveUsage;
        const effectiveStandardRate = effectiveUsage?.standardRate;
        const effectiveStandardCost = effectiveUsage?.standardCost;
        const effectiveMaxUnitsPct = effectiveUsage?.maxUnitsPct;
        const effectiveNonProjectWorkPct = effectiveUsage?.nonProjectWorkPct;

        const status = resource.attributes.Status;
        const stateInEntity = entityId ? calculateStateInEntity([resource], [entityId]) : undefined;
        const isInactive = status === ResourceStatus.Inactive || stateInEntity === ResourceEntityState.Inactive;

        const estimatedCharge = entityId ? (summary.estimatedChargeByEntity[entityId] ?? 0) : summary.estimatedCharge;
        const estimatedCost = entityId ? (summary.estimatedCostByEntity[entityId] ?? 0) : summary.estimatedCost;
        const actualCost = entityId ? (summary.actualCostByEntity[entityId] ?? 0) : summary.actualCost;

        return (
            <div className="timeline-tooltip">
                <div className="header">
                    <span className={isInactive ? 'inactive-persona' : undefined}>
                        <ImagePersonaCoin className="logo" size={PersonaSize.size32} info={resource} text={resource.name} />
                        {isInactive && <span className="inactive-icon" />}
                    </span>
                    <div className="title">
                        <div className="overflow-text" title={resource.name}>
                            {resource.name}
                        </div>
                    </div>
                    {!!entityId && <ResourceBookingType resource={resource} entityId={entityId} />}
                </div>
                <div className="content">
                    {status !== undefined && this.renderField("State", <StatusView status={status} />)}
                    {!!entityId && stateInEntity !== undefined
                        && this.renderField(`State in ${entityTypeLabelMap[entityType].singular}`, <StateInEntityView state={stateInEntity} />)}
                    {isSingleDay
                        ? this.renderField("Date", <div className="no-overflow ellipsis">
                            {FormatDate(startDate, { weekday: 'long' })}
                            {calendarException ? <> - <span title={calendarException.name}>{calendarException.name}</span></> : ''}
                        </div>)
                        : this.renderField("Period", <div>{FormatDate(startDate)} - {FormatDate(finishDate)}</div>)}
                    {this.renderField("Availability",
                        <div><span className={`progress-tip ${availability > 0 ? '' : 'gray'}`}>{formatValue(availability)}</span> hour{availability !== 1 ? 's' : ''}</div>)}
                    {this.renderField("Capacity", <div>{formatValue(capacityHours)} hour{capacityHours !== 1 ? 's' : ''}</div>)}
                    {isSingleDay && this.renderField("Max Units", <div>
                        {effectiveMaxUnitsPct !== undefined ? formatValue(effectiveMaxUnitsPct, FormatType.Percent) : <span className="non-completed">N/A</span>}
                    </div>)}
                    {isSingleDay && this.renderField("Non-Project Work", <div>
                        {effectiveNonProjectWorkPct !== undefined ? formatValue(effectiveNonProjectWorkPct, FormatType.Percent) : <span className="non-completed">N/A</span>}
                    </div>)}
                    {isSingleDay && this.renderField("Standard Rate", <div>
                        {effectiveStandardRate !== undefined ? formatFieldValue(effectiveStandardRate, FormatType.Cost, user, entityType, tenant) : <span className="non-completed">N/A</span>}
                    </div>)}
                    {isSingleDay && this.renderField("Standard Cost", <div>
                        {effectiveStandardCost !== undefined ? formatFieldValue(effectiveStandardCost, FormatType.Cost, user, entityType, tenant) : <span className="non-completed">N/A</span>}
                    </div>)}
                    {entityId
                        ? this.renderEntityWork()
                        : <>
                            {this.renderAllWork("Committed", committedWork, committedByEntity)}
                            {this.renderAllWork("Proposed", proposedWork, proposedByEntity)}
                        </>}
                    {this.renderField("Estimated Charge", <div>{formatFieldValue(estimatedCharge, FormatType.Cost, user, entityType, tenant, "EstimatedCharge")}</div>)}
                    {this.renderField("Estimated Cost", <div>{formatFieldValue(estimatedCost, FormatType.Cost, user, entityType, tenant, "EstimatedCost")}</div>)}
                    {this.renderField("Actual Cost", <div>{formatFieldValue(actualCost, FormatType.Cost, user, entityType, tenant, "ActualCost")}</div>)}
                    {this.renderField("Actual", <div>{formatValue(actualWork)} hour{actualWork !== 1 ? 's' : ''}</div>)}
                    {this.renderField("Overallocated", <div className='align-top'>
                        {overage !== 0
                            ? <div><span className="progress-tip red">{formatValue(overage)}</span>
                                {proposedOverage === overage ? ` hour${overage !== 1 ? 's' : ''}` : undefined}</div>
                            : hasLocalOverage
                                ? <div><Icon iconName="Warning" className="warning-icon" title="" />Some days</div>
                                : <div className="non-completed">n/a</div>}
                        {proposedOverage !== overage
                            ? <div>
                                &nbsp;/ <span className="progress-tip red">{formatValue(proposedOverage)}</span> hour{overage > 0 || proposedOverage > 0 ? 's' : ''} (if committed)
                            </div>
                            : !hasLocalOverage && hasLocalProposedOverage
                                ? <div>
                                    &nbsp;/ <Icon iconName="Warning" className="warning-icon" />Some days (if committed)
                                </div>
                                : undefined}
                    </div>)}
                    {!!this.props.onMenuRender && this.renderField("", this.props.onMenuRender(), 'menu')}
                </div>
            </div>
        );
    }

    private renderEntityWork = () => {
        const { entityId, showPerEntityWork, resource, entityType, resourcePlanningLevel } = this.props;
        const { summary, knownEntitiesMap } = this.state;
        const { committedByEntity, proposedByEntity } = summary;
        const committed = findUsage(resource.usage, entityId, _ => _.committed);
        const hours = (committed ? committedByEntity : proposedByEntity)[entityId!] ?? 0;

        const otherEntityIds = Object.keys(committedByEntity).filter(_ => _ !== entityId && !!committedByEntity[_]);
        const knownEntities = otherEntityIds.map(_ => knownEntitiesMap[_]).filter(notUndefined);
        const unknownEntityIds = otherEntityIds.filter(_ => !knownEntitiesMap[_]);
        const unknownEntityWork = unknownEntityIds.reduce((cum, cur) => (cum + committedByEntity[cur]), 0);
        return <>
            {this.renderField("Allocated",
                <div><span className="progress-tip gray">{formatValue(hours)}</span> hour{hours !== 1 ? 's' : ''} on this {entityType}</div>)}
            {showPerEntityWork && this.renderField("Other Work", <>
                {otherEntityIds.length
                    ? <div className="no-overflow">
                        {knownEntities.map(_ => <div key={_.id} className="ellipsis">
                            {formatValue(committedByEntity[_.id])} hour{committedByEntity[_.id] !== 1 ? 's' : ''} committed on {resourcePlanningLevel}
                            &nbsp;<Link href={`/${resourcePlanningLevel}/${_.id}`} title={_.attributes.Name}>{_.attributes.Name}</Link>
                        </div>)}
                        {unknownEntityWork !== 0 && unknownEntityIds.length > 0 && <div>
                            {formatValue(unknownEntityWork)} hour{unknownEntityWork !== 1 ? 's' : ''} committed on {unknownEntityIds!.length}
                            &nbsp;{knownEntities!.length ? 'more' : ''} {resourcePlanningLevel}{unknownEntityIds!.length !== 1 ? 's' : ''}
                        </div>}
                    </div>
                    : <div>0 hours</div>}
            </>, 'align-top')}
        </>;
    }

    private renderAllWork = (label: string, hours: number, hoursByEntity: Dictionary<number>) => {
        const { showPerEntityWork, resourcePlanningLevel } = this.props;
        const { knownEntitiesMap } = this.state;

        const entityIds = Object.keys(hoursByEntity).filter(_ => !!hoursByEntity[_]);
        const knownEntities = entityIds.map(_ => knownEntitiesMap[_]).filter(notUndefined);
        const unknownEntityIds = entityIds.filter(_ => knownEntitiesMap[_] === undefined);
        const unknownEntityWork = unknownEntityIds.reduce((cum, cur) => (cum + hoursByEntity[cur]), 0);
        const isOneEntity = knownEntities.length === 1 && entityIds.length === 1;
        const isAllEntitiesUnknown = knownEntities.length === 0 && unknownEntityWork !== 0;

        return this.renderField(label, <div className="no-overflow">
            <div>
                <span className="progress-tip gray">{formatValue(hours)}</span> hour{hours !== 1 ? 's' : ''}
                {showPerEntityWork && <>
                    {isOneEntity && <> on {resourcePlanningLevel} <Link href={`/${resourcePlanningLevel}/${knownEntities[0].id}`} title={knownEntities[0].attributes.Name}>
                        {knownEntities[0].attributes.Name}
                    </Link></>}
                    {isAllEntitiesUnknown && <> on {entityIds.length} {resourcePlanningLevel}{entityIds.length !== 1 ? 's' : ''}</>}
                </>}
            </div>
            {showPerEntityWork && !isOneEntity && !isAllEntitiesUnknown && <>
                {knownEntities.map(_ => <div key={_.id} className="ellipsis">
                    {formatValue(hoursByEntity[_.id])} hour{hoursByEntity[_.id] !== 1 ? 's' : ''} on {resourcePlanningLevel}&nbsp;
                    <Link href={`/${resourcePlanningLevel}/${_.id}`} title={_.attributes.Name}>{_.attributes.Name}</Link>
                </div>)}
                {unknownEntityWork !== 0 && <div>
                    {formatValue(unknownEntityWork)} hour{unknownEntityWork !== 1 ? 's' : ''} on {unknownEntityIds.length}
                    &nbsp;{knownEntities.length ? 'more' : ''} {resourcePlanningLevel}{unknownEntityIds.length !== 1 ? 's' : ''}
                </div>}
            </>}
        </div>, 'align-top');
    }

    private renderField = (name: string, valueElement: JSX.Element, className?: string): JSX.Element | null => {
        return (
            <div className={`item align-center ${className || ''}`}>
                {!!name && <div className="label">{name}</div>}
                {valueElement}
            </div>
        );
    }
}

function mapStateToProps(state: ApplicationState, ownProps: OwnProps): StateProps {
    return {
        baseResourceCalendar: getEntityCalendar(state.calendars, ownProps.resource, ownProps.resource.calendar.id),
        resourcePlanningLevel: mapServerEntityType[state.tenant.resourcePlanningSettings?.resourcePlanningLevel!]!,
        tenant: state.tenant,
        user: state.user,
        defaultCostRateTableId: getDefaultCostRateTable(state)?.id,
    };
}

export default connect(mapStateToProps)(UtilizationTooltipContent);